import { DOCUMENT, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';

import { SessionStorageService } from 'ngx-webstorage';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, tap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ThemeService {
    // #region Injects

    protected readonly breakpointObserver = inject(BreakpointObserver);
    protected readonly document = inject<Document>(DOCUMENT);
    protected readonly sessStorage = inject(SessionStorageService);

    // #endregion

    // #region Properties

    protected _currentTheme = signal<'light' | 'dark'>('light');
    currentTheme = this._currentTheme.asReadonly();
    protected _sysTheme = toSignal(
        this.breakpointObserver.observe('(prefers-color-scheme: dark)').pipe(
            map((d) => {
                return d?.matches ?? false;
            })
        )
    );

    // #endregion

    constructor() {
        const storedTheme = this.sessStorage.retrieve('current-theme');

        // If we have a stored theme, set it now.
        if (storedTheme) {
            this._currentTheme.set(storedTheme);
        } else {
            // Default to whatever is the current system default, but do not officially "set" this as a user-selected option.
            const sysDefaultIsDark = this.breakpointObserver.isMatched('(prefers-color-scheme: dark)');

            this._currentTheme.set(sysDefaultIsDark ? 'dark' : 'light');
            // this.sessStorage.store('current-theme', this.currentTheme());
        }

        // Effect to monitor when the system theme changes, so we can update the app theme automatically (if a user-defined choice has not been made).
        effect(() => {
            const systheme = this._sysTheme() ? 'dark' : 'light';

            untracked(() => {
                const storedTheme = this.sessStorage.retrieve('current-theme');

                if (!storedTheme && systheme !== this._currentTheme()) {
                    // Nothing is stored, so update accordingly.
                    this._currentTheme.set(systheme);
                }
            });
        });

        // Effect to keep the theme mode synchronized to the DOM via the body element.
        effect(() => {
            const curTheme = this._currentTheme();

            untracked(() => {
                // Update the signal and update the body element.
                if (curTheme === 'dark') {
                    this.document.body.classList.add('dark-mode');
                } else {
                    this.document.body.classList.remove('dark-mode');
                }
            });
        });
    }

    // #region Toggle

    toggle() {
        const curMode = this._currentTheme();

        this._currentTheme.set(curMode === 'light' ? 'dark' : 'light');

        // This is a user-manual event. We persist this in session storage now.
        this.sessStorage.store('current-theme', this._currentTheme());
    }

    // #endregion
}
